import { create } from "zustand";
import { Ticket, TicketChatMessage } from "./Ticket";
import {
  HttpFeatureRequestService,
} from "../Services/http.clients/http.feature.request.client";
import { HttpTicketService } from "../Services/http.clients/http.ticket.client";

const LIMIT = 10;

const markUpvoted = (tickets: Ticket[], upvotes: Set<string>): Ticket[] =>
  tickets.map((ticket) => {
    const isUpvoted = upvotes.has(ticket.id ?? "");
    return ticket.upvoted === isUpvoted ? ticket : { ...ticket, upvoted: isUpvoted };
  });

export type Sort = "createdAt" | "upvotesCount";

interface FeatureRequestState {
  loadRoadmap: () => void;
  fetchNextFeatureList: () => void;
  setFeatureSort: (sort: Sort) => void;
  setFeatureStatusFilter: (status?: string) => void;
  setCurrentFeatureRequest: (featureRequest?: Ticket) => void;
  loadFeatureRequest: (id: string) => Promise<{ ticket: Ticket, comments: TicketChatMessage[] }>;
  upvoteFeatureRequest: (shareToken: string, email: string) => Promise<void>;
  addComment: (shareToken: string, comment: string) => Promise<void>;
  updateTickets: (updater: (ticket: Ticket) => Ticket) => void;
  upvotes: string[];
  statusOptions: { value: string, label: string }[];
  currentFeatureRequest?: Ticket;
  currentComments: TicketChatMessage[];
  featureSort: Sort;
  featureStatusFilter?: string;
  featureListSkip: number;
  hasMoreFeatures: boolean;
  featureRequests: Ticket[];
}

export const useFeatureRequestStore = create<FeatureRequestState>()(
  (set, get) => ({
    currentFeatureRequest: undefined,
    featureRequests: [],
    featureSort: "upvotesCount" satisfies Sort,
    featureStatusFilter: undefined,
    featureListSkip: 0,
    hasMoreFeatures: true,
    currentComments: [],
    statusOptions: [],
    upvotes: [],
    setCurrentFeatureRequest: (featureRequest?: Ticket) => {
      // Clearing the request also clears its comments — leaving them made the
      // NEXT opened request show the previous one's comments while loading.
      set(
        featureRequest
          ? { currentFeatureRequest: featureRequest }
          : { currentFeatureRequest: undefined, currentComments: [] },
      );
    },
    updateTickets: (updater: (ticket: Ticket) => Ticket) => {
      const featureRequests = get().featureRequests;
      const updated = featureRequests.map(updater);
      const changed = updated.some((t, i) => t !== featureRequests[i]);
      if (changed) {
        set({ featureRequests: updated });
      }
    },
    fetchNextFeatureList: () => {
      const { featureListSkip, featureStatusFilter, featureSort } = get();
      const nextSkip = featureListSkip + LIMIT;
      set({ featureListSkip: nextSkip });

      const service = HttpFeatureRequestService.getInstance();
      service.getFeatureList({ skip: nextSkip, limit: LIMIT, sort: featureSort, status: featureStatusFilter })
        .then((data) => {
          if (data?.tickets) {
            const stamped = markUpvoted(data.tickets, new Set(get().upvotes));
            set({
              featureRequests: [...get().featureRequests, ...stamped],
              hasMoreFeatures: data.tickets.length >= LIMIT,
            });
          }
        });
    },
    setFeatureSort: (sort) => {
      set({ featureSort: sort, featureRequests: [], hasMoreFeatures: true, featureListSkip: 0 });
      get().loadRoadmap();
    },
    setFeatureStatusFilter: (status) => {
      set({ featureStatusFilter: status, featureRequests: [], hasMoreFeatures: true, featureListSkip: 0 });
      get().loadRoadmap();
    },
    upvoteFeatureRequest: async (shareToken: string, email: string) => {
      const service = HttpFeatureRequestService.getInstance();
      const { upvotes, featureRequests, currentFeatureRequest } = get();

      const ticket = featureRequests.find((t) => t.shareToken === shareToken);
      const ticketId = ticket?.id ?? currentFeatureRequest?.id;
      if (!ticketId) return;

      const wasUpvoted = upvotes.includes(ticketId);
      const delta = wasUpvoted ? -1 : 1;

      const newUpvotes = wasUpvoted
        ? upvotes.filter((id) => id !== ticketId)
        : [...upvotes, ticketId];

      set({
        upvotes: newUpvotes,
        ...(currentFeatureRequest?.id === ticketId
          ? {
            currentFeatureRequest: {
              ...currentFeatureRequest,
              upvoted: !wasUpvoted,
              upvotesCount: (currentFeatureRequest.upvotesCount ?? 0) + delta,
            },
          }
          : {}),
      });

      get().updateTickets((t) =>
        t.id === ticketId
          ? { ...t, upvoted: !wasUpvoted, upvotesCount: (t.upvotesCount ?? 0) + delta }
          : t
      );

      try {
        await service.upvoteFeatureRequest(shareToken, email);
      } catch (_) {
        set({ currentFeatureRequest, upvotes, featureRequests });
      }
    },
    addComment: async (shareToken: string, comment: string) => {
      const service = HttpFeatureRequestService.getInstance();
      try {
        const newComment = await service.addFeatureRequestComment(
          shareToken,
          comment
        );
        set({ currentComments: [...get().currentComments, newComment] });
      } catch (_) { }
    },
    loadFeatureRequest: async (shareToken: string) => {
      const service = HttpFeatureRequestService.getInstance();

      // Opening a (different) request: drop any previous one's data right
      // away so the detail view shows its loading skeleton instead of a stale
      // title/comments until the fetch lands.
      set({ currentFeatureRequest: undefined, currentComments: [] });

      const [ticket, comments] = await Promise.all([
        HttpTicketService.getInstance().findOne({ id: shareToken }),
        service.getFeatureRequestComments(shareToken).catch(() => [] as TicketChatMessage[]),
      ]);

      const { upvotes } = get();
      const upvoted = upvotes.includes(ticket?.id ?? "");

      set({ currentFeatureRequest: { ...ticket, upvoted }, currentComments: comments });
      return { ticket, comments };
    },
    async loadRoadmap() {
      const service = HttpFeatureRequestService.getInstance();
      const { featureStatusFilter } = get();

      try {
        const upvotes = await service.getSessionUpvotes();
        set({ upvotes: upvotes ?? [] });

        const data = await service.getRoadmapBoard(get().featureSort);
        if (data) {
          const upvoteSet = new Set(upvotes ?? []) as Set<string>;

          const statusOptions = data.map((board) => ({
            value: board?.column?.key,
            label: board?.column?.title,
          }));

          const activeFilter = featureStatusFilter ?? (data.length > 0 ? data[0]?.column?.key : undefined);
          const activeBoard = data.find((b) => b.column.key === activeFilter);
          const tickets = activeBoard ? markUpvoted(activeBoard.data.tickets, upvoteSet) : [];

          set({
            featureRequests: tickets,
            featureListSkip: 0,
            hasMoreFeatures: (activeBoard?.data?.count ?? 0) > tickets.length,
            statusOptions,
            ...(!featureStatusFilter && data.length > 0
              ? { featureStatusFilter: data[0]?.column?.key }
              : {}),
          });
        }
      } catch (exp) { }
    },
  })
);
